import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

import useStorefrontHome from "../../hooks/useStorefrontHome";
import useCart from "../../store/cart-store";
import Basket from "../Cart/Basket";
import StaticInfoBanner from "../Infobanner/StaticInfoBanner";
import NavBar from "../Navbar";
import Reviews from "../Reviews/Reviews";
import SideNavDrawer from "../SideNavDrawer";
import { Skeleton } from "../Skeleton";
import SkeletonCard from "../SkeletonCard";
import FeaturedCollectionWithProd from "./FeaturedCollectionWithProd";
import HomeHero from "./HomeHero";

const FeaturedHome = ({ theme, store, updateHomeActive, heroPhotos }) => {
  const hasHydrated = useCart((s) => s.hasHydrated);
  const setCart = useCart((s) => s.setCart);

  const products = useCart((s) => s.products);
  const cart = { products };

  const [showCart, setShowCart] = useState(false);
  const [showSideNav, setShowSideNav] = useState(false);

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  const updateAddedToCart = () => {};

  const handleShowCart = (boolVal = false) => {
    setShowCart(boolVal);
  };

  const {
    data: homeData,
    isLoading: isHomeInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useStorefrontHome(5, 4);

  const collectionsWithProducts = homeData?.pages?.flatMap((page) => page.collections || []) || [];
  const reviews = homeData?.pages?.[0]?.reviews || [];

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const renderLoadingSkeletons = (key) => (
    <div className="m-5" key={key}>
      <Skeleton className="mx-auto mb-[30px] mt-[50px] h-8 w-28 text-center font-bold text-primary" />
      <div className="hide-scrollbar flex flex-row gap-4 overflow-x-auto overflow-y-hidden whitespace-nowrap">
        {Array(2)
          .fill(0)
          .map((_, i) => (
            <SkeletonCard
              className="m-[4px_5px_5px_4px] w-[10px] rounded-[50px] px-5 py-[4px]"
              key={i}
              imageClass="w-[calc((var(--max-width)/1.5)-50px)] h-[calc((var(--max-width)/1.5)-50px)]"
            />
          ))}
      </div>
    </div>
  );

  return (
    <div className="font-montserrat">
      <StaticInfoBanner text={store?.announcement} />

      <div className="sticky top-0 z-50 flex h-full w-full justify-center">
        {hasHydrated && (
          <Basket
            isBasketOpen={showCart}
            setCart={setCart}
            cartData={cart}
            updateAddedToCart={updateAddedToCart}
            handleShowCart={handleShowCart}
            store={store}
            setRefresh={() => {}}
          />
        )}

        <NavBar
          cartActive={hasHydrated && cart.products.length > 0}
          hideInAdvance={false}
          homeActive={true}
          storeName={store?.name || ""}
          handleShowCart={handleShowCart}
          store={store}
          theme={theme}
          toggleSideNav={() => setShowSideNav(!showSideNav)}
          sideNavOpen={showSideNav}
          homePage
        />

        <SideNavDrawer
          isOpen={showSideNav}
          toggleDrawer={() => setShowSideNav(!showSideNav)}
          handleShowCart={handleShowCart}
          store={store}
        />
      </div>

      <HomeHero photos={heroPhotos} onShop={() => updateHomeActive(false)} />

      {(isHomeInitialLoading || collectionsWithProducts.length > 0) && (
        <div className="w-full">
          {isHomeInitialLoading ? (
            <div>
              {Array(2)
                .fill(0)
                .map((_, index) => renderLoadingSkeletons(`initial-skeleton-${index}`))}
            </div>
          ) : (
            <>
              {collectionsWithProducts.map((collection) => (
                <FeaturedCollectionWithProd
                  key={collection.id}
                  collectionTitle={collection.title || ""}
                  collectionHandle={collection.handle}
                  productsData={collection.products || []}
                />
              ))}

              {isFetchingNextPage && renderLoadingSkeletons("loading-more-skeleton")}

              {hasNextPage && <div ref={ref} className="h-12 w-full" aria-hidden="true" />}
            </>
          )}
        </div>
      )}

      <Reviews reviews={reviews} isLoading={isHomeInitialLoading} />
    </div>
  );
};

export default FeaturedHome;
